import { useEffect, useRef, useState } from 'react';
import { DatePicker } from '../../CompositeComponents/DatePicker/DatePicker';
import type { DateRange } from '../../CompositeComponents/DatePicker/types';
import { Spinner } from '../../BasicComponents/Spinner/Spinner';
import type { TimeRangeCustomValue, TimeRangeFilterProps, TimeRangePreset, TimeRangeValue } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DEFAULT_PRESETS: TimeRangePreset[] = [
  { id: 'today',     label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7',     label: 'Last 7 Days' },
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: 'custom',    label: 'Custom' },
];

function isCustomValue(v: TimeRangeValue): v is TimeRangeCustomValue {
  return 'from' in v && 'to' in v;
}

function formatDate(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function activePresetId(value: TimeRangeValue | undefined): string {
  if (!value) return 'today';
  return isCustomValue(value) ? 'custom' : (value as { preset: string }).preset;
}

function announcementText(value: TimeRangeValue | undefined, presets: TimeRangePreset[]): string {
  if (!value) return '';
  if (isCustomValue(value)) return `${formatDate(value.from)} – ${formatDate(value.to)} selected`;
  const preset = presets.find((p) => p.id === (value as { preset: string }).preset);
  return preset ? `${preset.label} selected` : '';
}

// ─── TimeRangeFilter ──────────────────────────────────────────────────────────

export function TimeRangeFilter({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  maxRange = 90,
  loading = false,
}: TimeRangeFilterProps) {
  const [customOpen, setCustomOpen] = useState(false);
  // Tracks the first date clicked inside the range picker so we can compute maxDate
  const [rangeAnchor, setRangeAnchor] = useState<Date | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // The DatePicker portals its calendar to <body> (issue #79), so a click on a day is outside
  // containerRef in the DOM — but inside this component in the React tree, where synthetic
  // events still bubble. The last mousedown seen that way is not an outside click.
  const insideEventRef = useRef<Event | null>(null);

  const activeId = activePresetId(value);
  const announcement = announcementText(value, presets);

  // Current custom range to pass to DatePicker (if active)
  const currentRange: DateRange | undefined =
    value && isCustomValue(value) ? { from: value.from, to: value.to } : undefined;

  // Custom label suffix shown on the button when a range is active
  const customRangeLabel =
    value && isCustomValue(value)
      ? `${formatDate(value.from)} – ${formatDate(value.to)}`
      : null;

  // maxDate for range picker: anchor + maxRange days
  const maxDate = rangeAnchor
    ? new Date(rangeAnchor.getFullYear(), rangeAnchor.getMonth(), rangeAnchor.getDate() + maxRange)
    : undefined;

  // Close custom picker on outside click
  useEffect(() => {
    if (!customOpen) return;
    function handlePointerDown(e: MouseEvent) {
      if (insideEventRef.current === e) return;
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setCustomOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [customOpen]);

  function handlePresetClick(preset: TimeRangePreset) {
    if (preset.id === 'custom') {
      setCustomOpen((o) => !o);
      return;
    }
    setCustomOpen(false);
    setRangeAnchor(null);
    onChange({ preset: preset.id });
  }

  function handleRangeChange(range: DateRange) {
    setRangeAnchor(null);
    setCustomOpen(false);
    onChange({ from: range.from, to: range.to });
  }

  return (
    <div
      ref={containerRef}
      onMouseDown={(e) => { insideEventRef.current = e.nativeEvent; }}
      className="relative flex flex-col gap-1 w-full"
    >
      {/* Preset button row */}
      <div
        role="radiogroup"
        aria-label="Time range"
        className="flex flex-wrap gap-1"
      >
        {presets.map((preset) => {
          const isActive = activeId === preset.id;
          const isCustomActive = preset.id === 'custom' && isActive && customRangeLabel;
          const isCustom = preset.id === 'custom';

          const button = (
            <button
              key={isCustom ? undefined : preset.id}
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-pressed={isActive}
              onClick={() => handlePresetClick(preset)}
              className={[
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors duration-150',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1',
                isActive
                  ? 'bg-brand-500 text-white'
                  : [
                      'bg-white dark:bg-neutral-800',
                      'text-neutral-700 dark:text-neutral-300',
                      'border border-neutral-300 dark:border-neutral-600',
                      'hover:border-neutral-400 dark:hover:border-neutral-500',
                      'hover:bg-neutral-50 dark:hover:bg-neutral-700',
                    ].join(' '),
              ].join(' ')}
            >
              {isCustomActive ? `Custom: ${customRangeLabel}` : preset.label}
              {loading && isActive && (
                <Spinner size="sm" variant="inline" label="Loading" />
              )}
            </button>
          );

          if (!isCustom) {
            return button;
          }

          // Anchor the custom date-range popover to the Custom button itself so
          // it opens directly below that button (right-aligned so it doesn't
          // overflow the page) instead of jumping to the left edge of the
          // surrounding filter bar (issue #115).
          return (
            <div key={preset.id} className="relative">
              {button}
              {customOpen && (
                <div className="absolute top-full right-0 mt-1 z-[60]">
                  <DatePicker
                    variant="range"
                    value={currentRange}
                    onChange={handleRangeChange}
                    maxDate={maxDate}
                    presets={[]}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Live region — announces selection to screen readers */}
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </span>
    </div>
  );
}
